/**
 * Site header — brand, navigation, local clock and burger, plus the scroll
 * progress hairline. The markup is the server's (templates/site/chrome/
 * header.html.twig); this module keeps the clock and the motion.
 *
 * @module chrome/header
 */

import { REDUCED_MOTION } from '../config.js';
import { $ } from '../lib/dom.js';
import { ScrollTrigger, gsap } from '../lib/gsap.js';
import { scrollToTarget } from '../lib/scroll.js';

/**
 * Hides the header while the story plays, so it never sits on the stage —
 * never while the menu is open: the burger lives in there.
 *
 * @param {boolean} hidden
 */
export function tuckHeader(hidden) {
    const header = $('.site-header');
    const menuOpen = $('[data-burger][aria-expanded="true"]');

    header?.classList.toggle('is-tucked', hidden && !menuOpen);
}

/** The local time of the studio, refreshed every 20 seconds. */
function startClock() {
    const clock = $('[data-clock]');

    if (!clock) {
        return;
    }

    const format = new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Luxembourg',
    });

    const tick = () => {
        clock.textContent = format.format(new Date());
    };

    tick();
    window.setInterval(tick, 20000);
}

export function animateHeader() {
    const header = $('.site-header');
    const progress = $('[data-progress]');
    const pulse = $('[data-pulse]');
    const toTop = $('[data-to-top]');

    // A backdrop once the page has left the top.
    if (header) {
        ScrollTrigger.create({
            start: 'top -80',
            end: 99999,
            onToggle: (self) => header.classList.toggle('is-stuck', self.isActive),
        });
    }

    if (progress) {
        gsap.to(progress, { scaleX: 1, ease: 'none', scrollTrigger: { start: 0, end: 'max', scrub: 0.3 } });
    }

    if (pulse && !REDUCED_MOTION) {
        gsap.to(pulse, { boxShadow: '0 0 0 8px rgba(31,158,82,0)', duration: 1.8, repeat: -1, ease: 'power2.out' });
    }

    startClock();

    toTop?.addEventListener('click', (event) => {
        event.preventDefault();
        scrollToTarget(0);
    });
}

/**
 * The header reads what lies under it on each side: paper or night under
 * the brand (left), under the nav or the burger (right). The split screens
 * put paper on one side and night on the other.
 */
export function watchHeaderBackground() {
    const header = $('.site-header');

    if (!header || !document.elementsFromPoint) {
        return;
    }

    const brand = $('.brand', header);
    const nav = $('.site-header__nav', header);
    const burger = $('[data-burger]', header);

    /**
     * @param {Element | null} element
     * @returns {boolean} Is the page under its centre at night?
     */
    const nightUnder = (element) => {
        if (!element) {
            return false;
        }

        const box = element.getBoundingClientRect();

        if (!box.width || box.bottom < 0) {
            return false;
        }

        const below = document
            .elementsFromPoint(box.left + box.width / 2, box.top + box.height / 2)
            .find((hit) => !hit.closest('.site-header, .chapter-nav, .site-menu'));

        return Boolean(below?.closest('[data-night]'));
    };

    const update = () => {
        const menuOpen = burger?.getAttribute('aria-expanded') === 'true';
        const right = nav?.offsetParent ? nav : burger;

        header.classList.toggle('is-night-left', !menuOpen && nightUnder(brand));
        header.classList.toggle('is-night-right', !menuOpen && nightUnder(right));
    };

    // A few times a second is plenty, and follows ScrollSmoother's easing.
    let frame = 0;

    gsap.ticker.add(() => {
        frame = (frame + 1) % 8;

        if (frame === 0) {
            update();
        }
    });

    update();
}
